import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "@posthog/react/slim";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Download, ExternalLink, Eye, FileCode, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Faq } from "@/components/faq";
import { FileUpload } from "@/components/file-upload";
import { SelectedJobCard } from "@/components/jobs/selected-job-card";
import { PageHero } from "@/components/page-hero";
import { PageLayout } from "@/components/page-layout";
import { ToolSwitcher } from "@/components/tool-switcher";
import { useAuthModal } from "@/context/auth-modal";
import { getUser } from "@/functions/get-user";
import { client } from "@/utils/orpc";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Checkbox } from "@super-resume-new/ui/components/checkbox";
import { Input } from "@super-resume-new/ui/components/input";
import { Label } from "@super-resume-new/ui/components/label";
import { ScrollArea } from "@super-resume-new/ui/components/scroll-area";
import { Spinner } from "@super-resume-new/ui/components/spinner";
import { Textarea } from "@super-resume-new/ui/components/textarea";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/ats-booster")({
  validateSearch: (search: Record<string, unknown>): { job?: string } => ({
    job:
      typeof search.job === "string"
        ? search.job
        : typeof search.jobId === "string"
          ? (search.jobId as string)
          : undefined,
  }),
  beforeLoad: async () => {
    const session = await getUser();
    return { session };
  },
  head: () =>
    seo({
      title: "ATS Keyword Injector for PDF Resumes | JobCues",
      description:
        "Embed the job description's keywords into your PDF resume's text layer without changing a pixel of the visual design, so keyword-matching filters read them.",
      path: "/ats-booster",
    }),
  component: AtsBoosterComponent,
});

/**
 * The default Button paints a violet border and shadow from --primary, so the booster's
 * emerald fill needs the edge recoloured too or the old outline shows through.
 */
const EMERALD_BUTTON =
  "cursor-pointer border-emerald-600 bg-emerald-600 text-white shadow-emerald-600/24 hover:border-emerald-700 hover:bg-emerald-700";

interface StealthResult {
  base64: string;
  filename: string;
  contentType: string;
  parsedAtsText: string;
  analysis: {
    extracted_keywords: Array<{
      category: string;
      keyword: string;
      importance: "critical" | "recommended" | "optional";
    }>;
    contextual_phrases: string[];
    formatted_stealth_text: string;
    match_summary: {
      ats_score_estimate: number;
      missing_critical_keywords: string[];
      summary_notes: string;
    };
  };
}

function AtsBoosterComponent() {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const { session } = Route.useRouteContext();
  const { openAuthModal } = useAuthModal();
  const { job: jobSlugParam } = Route.useSearch();
  const navigate = Route.useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [jobTitle, setJobTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [optionalKeywords, setOptionalKeywords] = useState("");
  const [addToTracker, setAddToTracker] = useState(true);

  const [selectedJob, setSelectedJob] = useState<{
    id: string;
    slug: string;
    title: string;
    company: string | null;
    workMode: string | null;
    locationPrimary: string | null;
    seniority: string | null;
    compMin: number | null;
    compMax: number | null;
    compCurrency: string | null;
    compPeriod: string | null;
    applyUrl?: string | null;
    rawText: string;
  } | null>(null);

  // Fetch job details if job slug/id query param is present
  const { data: jobResponse } = useQuery({
    queryKey: ["job", jobSlugParam],
    queryFn: async () => client.jobs.getBySlug({ slug: jobSlugParam! }),
    enabled: !!jobSlugParam,
  });

  useEffect(() => {
    if (jobResponse?.job) {
      setSelectedJob(jobResponse.job as any);
      setJobTitle(jobResponse.job.title);
      setJobDescription(jobResponse.job.rawText || "");
      toast.success(`Selected job: ${jobResponse.job.title}`);
    }
  }, [jobResponse]);

  const handleClearSelectedJob = () => {
    setSelectedJob(null);
    setJobTitle("");
    setJobDescription("");
    navigate({ search: (prev) => ({ ...prev, job: undefined }), replace: true });
  };

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<StealthResult | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"visual" | "ats">("visual");

  const prevUrlRef = useRef<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please upload your PDF resume file.");
      return;
    }
    if (!jobTitle.trim() || !jobDescription.trim()) {
      toast.error("Please fill in both Job Title and Job Description.");
      return;
    }

    posthog.capture("ats_boost_started", {
      added_to_tracker: addToTracker,
      job_source: selectedJob ? "directory" : "manual",
    });
    setLoading(true);
    setResult(null);

    try {
      let base64File: string | undefined = undefined;
      let fileType: "pdf" | "docx" | "doc" | undefined = undefined;

      if (file) {
        const arrayBuffer = await file.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = "";
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        base64File = btoa(binary);

        const ext = file.name.split(".").pop()?.toLowerCase();
        fileType = ext === "docx" ? "docx" : ext === "doc" ? "doc" : "pdf";
      }

      const parsedKeywords = optionalKeywords
        .split(/[,;\n]+/)
        .map((k) => k.trim())
        .filter(Boolean);

      const idempotencyKey = crypto.randomUUID();
      const res = await client.resume.injectStealthKeywords({
        file_base64: base64File,
        file_type: fileType,
        job_title: jobTitle,
        job_description: jobDescription,
        // Only link when the text is still the posting's own — an edited
        // description has to be stored, not thrown away.
        job_slug:
          selectedJob && jobDescription === selectedJob.rawText ? selectedJob.slug : undefined,
        optional_keywords: parsedKeywords.length > 0 ? parsedKeywords : undefined,
        add_to_tracker: addToTracker,
        idempotency_key: idempotencyKey,
      });

      setResult(res as StealthResult);
      posthog.capture("ats_boost_completed", {
        added_to_tracker: addToTracker,
        estimated_match_score: res.analysis.match_summary.ats_score_estimate,
      });

      const byteCharacters = atob(res.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: res.contentType });

      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current);
      }
      const newUrl = URL.createObjectURL(blob);
      prevUrlRef.current = newUrl;
      setPdfUrl(newUrl);

      toast.success("Resume boosted! Target keywords embedded invisibly into PDF layer.");
      queryClient.invalidateQueries({ queryKey: ["credits"] });
      if (addToTracker) {
        queryClient.invalidateQueries({ queryKey: ["tracker", "entries"] });
      }
    } catch (err: any) {
      console.error(err);
      if (err?.message?.includes("Insufficient credits")) {
        toast.error("Insufficient credits. Please purchase more credits to continue.");
      } else {
        toast.error(err?.message || "Failed to process resume. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    posthog.capture("ats_boosted_pdf_downloaded");
    const link = document.createElement("a");
    link.href = `data:${result.contentType};base64,${result.base64}`;
    link.download = result.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = () => {
    setFile(null);
    setJobTitle("");
    setJobDescription("");
    setOptionalKeywords("");
    setResult(null);
    if (prevUrlRef.current) {
      URL.revokeObjectURL(prevUrlRef.current);
      prevUrlRef.current = null;
    }
    setPdfUrl(null);
  };

  const canSubmit =
    !loading && !!file && jobTitle.trim().length > 0 && jobDescription.trim().length > 0;

  return (
    <PageLayout id="main-content" className="max-w-7xl" variant="emerald">
      <PageHero
        eyebrow="ATS booster"
        icon={Zap}
        accent="emerald"
        title={
          <>
            <strong>Pass ATS screening</strong>
            <br />
            with the right keywords
          </>
        }
        description="Embed the job's keywords into your PDF resume without changing how it looks."
      />

      {/* Tool Switcher */}
      <ToolSwitcher current="ats-booster" />

      {/* Form / Inputs Grid */}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left: Job Description & Keywords Input */}
          <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-foreground">Target Job</h2>
                {selectedJob && (
                  <Badge variant="secondary" className="text-[10px]">
                    Verified Job
                  </Badge>
                )}
              </div>
              {!selectedJob && (
                <Link
                  to="/jobs"
                  className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  Browse Jobs Directory &rarr;
                </Link>
              )}
            </div>

            {selectedJob ? (
              <SelectedJobCard
                job={selectedJob}
                accent="emerald"
                onClear={handleClearSelectedJob}
              />
            ) : (
              /* Manual Freeform Job Title & Description Inputs */
              <>
                <div className="flex flex-col gap-1.5">
                  <Label
                    htmlFor="ats-job-title"
                    className="text-xs font-semibold text-muted-foreground"
                  >
                    Job Title <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="ats-job-title"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g. Senior Frontend Engineer"
                    disabled={loading}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label
                    htmlFor="ats-job-desc"
                    className="text-xs font-semibold text-muted-foreground"
                  >
                    Job Description <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="ats-job-desc"
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Paste the full job description here…"
                    disabled={loading}
                    rows={6}
                    className="resize-none text-xs leading-relaxed"
                  />
                </div>
              </>
            )}

            <div className="flex flex-col gap-1.5">
              <Label
                htmlFor="ats-opt-keywords"
                className="text-xs font-semibold text-muted-foreground"
              >
                Additional / Custom Keywords (Optional)
              </Label>
              <Input
                id="ats-opt-keywords"
                value={optionalKeywords}
                onChange={(e) => setOptionalKeywords(e.target.value)}
                placeholder="e.g. AWS, Kubernetes, Next.js (comma-separated)"
                disabled={loading}
              />
            </div>

            {/* Auto-Add to Job Tracker Checkbox */}
            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="add-tracker"
                checked={addToTracker}
                onCheckedChange={(checked) => setAddToTracker(checked === true)}
                disabled={loading}
              />
              <Label
                htmlFor="add-tracker"
                className="text-xs font-normal text-muted-foreground cursor-pointer"
              >
                Automatically create an entry in my{" "}
                <Link
                  to="/job-tracker"
                  className="font-semibold text-foreground underline underline-offset-2"
                >
                  Application Tracker
                </Link>
              </Label>
            </div>

            {/* Submit Action */}
            <div className="mt-2 flex items-center gap-2">
              {(file || jobTitle || jobDescription || result) && (
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={handleReset}
                  disabled={loading}
                  className="cursor-pointer"
                >
                  Reset
                </Button>
              )}
              {session ? (
                <Button
                  type="submit"
                  size="lg"
                  disabled={!canSubmit}
                  className={`flex-1 gap-2 ${EMERALD_BUTTON}`}
                >
                  <Zap className="size-4" aria-hidden="true" />
                  {loading ? "Injecting Keywords…" : "Boost ATS Match Score"}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="lg"
                  onClick={() => openAuthModal({ defaultTab: "signup" })}
                  className={`flex-1 gap-2 ${EMERALD_BUTTON}`}
                >
                  Sign up to Continue
                </Button>
              )}
            </div>
          </div>

          {/* Right: Resume File Input */}
          <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">Your Resume (PDF)</h2>
              <Badge variant="outline" className="text-xs">
                Visuals Preserved
              </Badge>
            </div>

            <FileUpload onFile={setFile} disabled={loading} accent="emerald" />

            <p className="text-xs text-muted-foreground leading-relaxed">
              Upload your original PDF resume. The ATS Booster injects machine-readable search terms
              directly into the PDF stream so Applicant Tracking Systems extract every target
              keyword.
            </p>
          </div>
        </div>
      </form>

      {/* Loading Progress State */}
      {loading && (
        <div className="mt-8 flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-border bg-card p-8 text-center shadow-sm">
          <Spinner className="mb-4 size-6 text-emerald-500" />
          <h3 className="text-sm font-semibold text-foreground">Analysing the job requirements</h3>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Extracting high-priority criteria & embedding invisible keyword layer into PDF…
          </p>
        </div>
      )}

      {/* Boosted Results View */}
      {result && (
        <div className="mt-10 flex flex-col gap-6">
          {/* ATS Intelligence Summary Card */}
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-foreground">ATS Match Score Summary</h3>
                  <Badge
                    variant="secondary"
                    className="border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[11px] text-emerald-500"
                  >
                    Estimated Match: {result.analysis.match_summary.ats_score_estimate}%
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  {result.analysis.match_summary.summary_notes}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {selectedJob?.applyUrl && (
                  <a href={selectedJob.applyUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="default" className="cursor-pointer gap-1.5">
                      Apply on Portal
                      <ExternalLink className="size-3.5" />
                    </Button>
                  </a>
                )}
                <Button
                  onClick={handleDownload}
                  size="default"
                  className={`shrink-0 gap-2 ${EMERALD_BUTTON}`}
                >
                  <Download className="size-4" />
                  Download Boosted PDF
                </Button>
              </div>
            </div>

            {/* Action Banner for Outbound Application */}
            {selectedJob?.applyUrl && (
              <div className="mt-4 flex flex-col items-start justify-between gap-3 rounded-xl border border-border bg-muted/40 p-4 sm:flex-row sm:items-center">
                <div>
                  <h4 className="text-sm font-semibold text-foreground">
                    Ready to submit your ATS-boosted resume?
                  </h4>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Apply directly on{" "}
                    {selectedJob.company ? `${selectedJob.company}'s` : "the company's"} official
                    job portal with your boosted PDF.
                  </p>
                </div>
                <a
                  href={selectedJob.applyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 w-full sm:w-auto"
                >
                  <Button size="sm" className={`w-full gap-1.5 sm:w-auto ${EMERALD_BUTTON}`}>
                    Apply on Portal
                    <ExternalLink className="size-3.5" />
                  </Button>
                </a>
              </div>
            )}

            {/* Categorized Injected Keywords */}
            <div className="mt-4 flex flex-col gap-2.5">
              <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Target Keywords Embedded ({result.analysis.extracted_keywords.length})
              </h4>
              <div className="max-h-[140px] w-full overflow-y-auto rounded-xl border border-border bg-muted/40 p-3">
                <div className="flex flex-wrap gap-1.5">
                  {result.analysis.extracted_keywords.map((kw, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="text-xs shrink-0 py-0.5 px-2 font-medium bg-card"
                    >
                      <CheckCircle2
                        className="mr-1 size-3 shrink-0 text-emerald-500"
                        aria-hidden="true"
                      />
                      <span>{kw.keyword}</span>
                      <span className="ml-1 text-[10px] text-muted-foreground">
                        ({kw.category})
                      </span>
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Dual Preview: Visual PDF vs ATS Machine Reader View */}
          <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/20 px-4 py-2.5">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab("visual")}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition cursor-pointer ${
                    activeTab === "visual"
                      ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
                      : "border border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Eye className="size-3.5" />
                  Visual PDF Preview
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("ats")}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition cursor-pointer ${
                    activeTab === "ats"
                      ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
                      : "border border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <FileCode className="size-3.5" />
                  ATS Reader View
                </button>
              </div>
            </div>

            <div className="h-[620px] w-full p-2 bg-background">
              {activeTab === "visual" && pdfUrl && (
                <iframe
                  src={pdfUrl}
                  title="Visual Resume PDF Preview"
                  className="h-full w-full rounded-xl border border-border"
                />
              )}

              {activeTab === "ats" && (
                <ScrollArea className="h-full w-full rounded-xl border border-border bg-muted/50 p-4 text-xs font-mono text-foreground">
                  <div className="mb-4 rounded-xl border border-border bg-muted/40 p-3 text-foreground">
                    <p className="flex items-center gap-1.5 text-xs font-semibold">
                      <CheckCircle2 className="size-4 text-emerald-500" aria-hidden="true" />
                      Target keywords embedded
                    </p>
                    <p className="mt-1.5 font-mono text-[11px] leading-relaxed opacity-90">
                      &quot;{result.analysis.formatted_stealth_text}&quot;
                    </p>
                  </div>
                  <h4 className="mb-2 font-semibold text-muted-foreground">
                    Extracted ATS Machine Text Preview:
                  </h4>
                  <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-foreground">
                    {result.parsedAtsText}
                  </pre>
                </ScrollArea>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Help / FAQ */}
      <div className="mt-16">
        <Faq />
      </div>
    </PageLayout>
  );
}
